import {
  ChangeDetectionStrategy,
  Component,
  inject,
  linkedSignal,
  ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  WcgButtonComponent,
  WcgDialogComponent,
} from '@wcg/frontend-framework';

import {
  AddressFormComponent,
  flattenAddressGroup,
  generateAddressFormGroup,
  AddressDialogData,
} from '@erm/cluster';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    AddressFormComponent,
    WcgDialogComponent,
    WcgButtonComponent,
  ],
  selector: 'wcg-address-dialog',
  styleUrls: ['./address.dialog.component.css'],
  template: `
    <wcg-dialog
      [title]="data.facadeService.addressTitle"
      [size]="data.facadeService.dialogSize"
    >
      <div class="wcg-full address-dialog__content p-6" content>
        <wcg-address-form
          [formGroup]="addressFormGroup"
          [addressLineLabels]="data.facadeService.addressLines"
          [currentAddress]="data.currentAddress!"
          [addressMode]="data.mode"
          [countries]="countries().data"
          [states]="states().data"
          (countryId)="fetchStates($event)"
        />
      </div>
      <div tertiary>
        <wcg-button color="tertiary" (click)="cancel()"> Cancel </wcg-button>
      </div>
      <div primary>
        <wcg-button
          color="primary"
          (click)="save()"
          [isDisabled]="addressFormGroup.invalid"
        >
          Save
        </wcg-button>
      </div>
    </wcg-dialog>
  `,
})
export class WcgAddressDialogComponent {
  private readonly _dialogRef = inject(MatDialogRef<WcgAddressDialogComponent>);
  public readonly data = inject<AddressDialogData>(MAT_DIALOG_DATA);

  addressFormGroup = generateAddressFormGroup();

  countries = linkedSignal({
    computation: (list) => {
      const countries = list.data.map((country) => ({
        id: country.id,
        label: country.name,
      }));
      return {
        data: countries,
        total: list.total,
      };
    },
    source: this.data.facadeService.$countries,
  });

  states = linkedSignal({
    computation: (list) => {
      const states = list.data.map((state) => ({
        id: state.id,
        label: state.name,
      }));

      return {
        data: states,
        total: list.total,
      };
    },
    source: this.data.facadeService.$states,
  });

  cancel(): void {
    this._dialogRef.close();
  }

  save(): void {
    this._dialogRef.close({
      ...flattenAddressGroup(
        this.addressFormGroup,
        this.data.facadeService.$countries().data,
        this.data.mode,
        this.data.currentAddress?.id,
      ),
      id: this.data.currentAddress?.id,
    });
  }

  fetchStates(countryId: string): void {
    if (!countryId.length) {
      return;
    }
    this.data.facadeService.fetchStates(countryId);
  }
}
